import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Activity,
  Clock,
  Hash,
  Users,
  Zap,
  Copy,
  ExternalLink,
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Database,
  Server,
  Cpu,
  HardDrive,
  Eye,
  Download,
  RefreshCw
} from 'lucide-react';

interface BlockDetail {
  number: number;
  hash: string;
  parentHash: string;
  timestamp: string;
  miner: string;
  difficulty: string;
  totalDifficulty: string;
  size: number;
  gasLimit: number;
  gasUsed: number;
  gasUtilization: number;
  baseFeePerGas: number;
  transactionCount: number;
  uncleCount: number;
  nonce: string;
  mixHash: string;
  stateRoot: string;
  transactionsRoot: string;
  receiptsRoot: string;
  logsBloom: string;
  extraData: string;
  reward: string;
  transactions: Transaction[];
}

interface Transaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  gasUsed: number;
  gasPrice: number;
  status: 'success' | 'failed';
  type: number;
  nonce: number;
  transactionIndex: number;
}

const BlockDetailPage: React.FC = () => {
  const { blockNumber } = useParams<{ blockNumber: string }>();
  const [block, setBlock] = useState<BlockDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions'>('overview');

  useEffect(() => {
    if (blockNumber) {
      loadBlockDetail(blockNumber);
    }
  }, [blockNumber]);

  const loadBlockDetail = async (blockNum: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // 실제 API 호출
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/explorer/blocks/${blockNum}`);
      
      if (!response.ok) {
        throw new Error(`블록 조회 실패: ${response.status}`);
      }
      
      const data = await response.json();
      
      console.log('🔍 블록 상세 API 응답:', data);
      
      if (!data.success || !data.block) {
        console.error('❌ 블록 데이터 없음:', data);
        throw new Error('블록 데이터를 찾을 수 없습니다.');
      }
      
      const blockData = data.block;
      console.log('✅ 블록 데이터:', {
        number: blockData.number,
        hash: blockData.hash?.substring(0, 20),
        transactions: blockData.transactions?.length
      });
      
      // 트랜잭션 데이터 매핑
      const transactions: Transaction[] = (blockData.transactions || []).map((tx: any, index: number) => ({
        hash: tx.hash,
        from: tx.from || '0x0000000000000000000000000000000000000000',
        to: tx.to || null,
        value: parseFloat(tx.value || '0').toFixed(4),
        gasUsed: parseInt(tx.gasUsed || '0'),
        gasPrice: parseFloat(tx.gasPrice || '0') / 1e9, // Gwei로 변환
        status: tx.status || 'pending',
        type: tx.type === 'contract' ? 1 : 0, // 0: transfer, 1: contract
        nonce: tx.nonce || 0,
        transactionIndex: tx.transactionIndex !== undefined ? tx.transactionIndex : index
      }));
      
      // 블록 데이터 매핑
      const blockDetail: BlockDetail = {
        number: blockData.number,
        hash: blockData.hash,
        parentHash: blockData.parentHash,
        timestamp: new Date(blockData.timestamp * 1000).toISOString(),
        miner: blockData.miner || blockData.validator,
        difficulty: '0', // polygon-edge는 difficulty를 사용하지 않음
        totalDifficulty: '0',
        size: parseInt(blockData.size || '0'),
        gasLimit: parseInt(blockData.gasLimit || '0'),
        gasUsed: parseInt(blockData.gasUsed || '0'),
        gasUtilization: blockData.gasLimit ? (parseInt(blockData.gasUsed) / parseInt(blockData.gasLimit)) * 100 : 0,
        baseFeePerGas: parseFloat(blockData.baseFeePerGas || '0') / 1e9, // Gwei로 변환
        transactionCount: blockData.transactionCount || transactions.length,
        uncleCount: 0, // polygon-edge는 uncle을 사용하지 않음
        nonce: '0x0',
        mixHash: blockData.hash, // mixHash 대신 hash 사용
        stateRoot: blockData.hash, // stateRoot는 블록체인에서 직접 가져올 수 없음
        transactionsRoot: blockData.hash,
        receiptsRoot: blockData.hash,
        logsBloom: '0x' + '0'.repeat(512),
        extraData: blockData.extraData || '0x',
        reward: blockData.reward || '2.0',
        transactions: transactions
      };

      setBlock(blockDetail);
      setLoading(false);
    } catch (err) {
      console.error('블록 상세 정보 로딩 실패:', err);
      setError('블록 정보를 불러오는데 실패했습니다.');
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('클립보드에 복사되었습니다.');
  };

  const formatHash = (hash: string, length: number = 10) => {
    return `${hash.slice(0, length)}...${hash.slice(-8)}`;
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error || !block) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">블록을 찾을 수 없습니다</h2>
        <p className="text-gray-600 mb-4">{error || '요청한 블록이 존재하지 않습니다.'}</p>
        <Link
          to="/blocks"
          className="inline-flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>블록 목록으로 돌아가기</span>
        </Link>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/blocks"
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">블록 #{formatNumber(block.number)}</h1>
              <p className="text-gray-600 mt-1">{formatDate(block.timestamp)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => loadBlockDetail(blockNumber!)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>새로고침</span>
            </button>
            <button
              onClick={() => copyToClipboard(block.hash)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              <Copy className="w-4 h-4" />
              <span>해시 복사</span>
            </button>
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', name: '개요', icon: Activity },
              { id: 'transactions', name: '트랜잭션', icon: Zap }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                  {tab.id === 'transactions' && (
                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                      {block.transactionCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* 기본 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">블록 높이</p>
                      <p className="text-2xl font-bold text-gray-900">{formatNumber(block.number)}</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Database className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">트랜잭션</p>
                      <p className="text-2xl font-bold text-gray-900">{block.transactionCount}</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-lg">
                      <Zap className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">가스 사용률</p>
                      <p className="text-2xl font-bold text-gray-900">{block.gasUtilization.toFixed(1)}%</p>
                    </div>
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <Cpu className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">블록 크기</p>
                      <p className="text-2xl font-bold text-gray-900">{formatBytes(block.size)}</p>
                    </div>
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <HardDrive className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* 상세 정보 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">블록 정보</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600">해시:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-mono text-gray-900">{formatHash(block.hash)}</span>
                        <button
                          onClick={() => copyToClipboard(block.hash)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600">부모 해시:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-mono text-gray-900">{formatHash(block.parentHash)}</span>
                        <button
                          onClick={() => copyToClipboard(block.parentHash)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">타임스탬프:</span>
                      <span className="text-sm text-gray-900">{formatDate(block.timestamp)}</span>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600">채굴자:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-mono text-gray-900">{formatHash(block.miner)}</span>
                        <button
                          onClick={() => copyToClipboard(block.miner)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">난이도:</span>
                      <span className="text-sm text-gray-900">{block.difficulty}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">총 난이도:</span>
                      <span className="text-sm text-gray-900">{block.totalDifficulty}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">보상:</span>
                      <span className="text-sm font-medium text-green-600">{block.reward} VTTN</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">가스 정보</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">가스 한도:</span>
                      <span className="text-sm text-gray-900">{formatNumber(block.gasLimit)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">가스 사용량:</span>
                      <span className="text-sm text-gray-900">{formatNumber(block.gasUsed)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">가스 사용률:</span>
                      <span className="text-sm text-gray-900">{block.gasUtilization.toFixed(2)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(block.gasUtilization, 100)}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">기본 수수료:</span>
                      <span className="text-sm text-gray-900">{block.baseFeePerGas.toFixed(2)} Gwei</span>
                    </div>
                  </div>

                  <h4 className="text-md font-semibold text-gray-900 mt-6 mb-3">기타 정보</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">블록 크기:</span>
                      <span className="text-sm text-gray-900">{formatBytes(block.size)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">엉클 수:</span>
                      <span className="text-sm text-gray-900">{block.uncleCount}</span>
                    </div>
                    <div className="flex justify-between items-start">
                      <span className="text-sm font-medium text-gray-600">논스:</span>
                      <span className="text-sm font-mono text-gray-900">{block.nonce}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  트랜잭션 ({block.transactionCount}개)
                </h3>
                <button className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                  <Download className="w-4 h-4" />
                  <span>CSV 다운로드</span>
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">해시</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">값</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">가스</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">액션</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {block.transactions.map((tx) => (
                      <tr key={tx.hash} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-mono text-orange-600">{formatHash(tx.hash)}</span>
                            <button
                              onClick={() => copyToClipboard(tx.hash)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono text-gray-900">{formatHash(tx.from)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono text-gray-900">{formatHash(tx.to)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">{tx.value} VTTN</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{formatNumber(tx.gasUsed)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                            tx.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {tx.status === 'success' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            <span className="capitalize">{tx.status}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={`/tx/${tx.hash}`}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg"
                            title="상세 보기"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default BlockDetailPage;